import { ISite } from "../models/Sites";

const CommonUtils = {
  fahrenheitToCelsius(fahrenheit: number, accuracy: number) {
    const accuracyMultiplier = Math.pow(10, accuracy);

    return Math.round((((fahrenheit - 32) * 5) / 9) * accuracyMultiplier) / accuracyMultiplier;
  },

  celsiusToFahrenheit(celsius: number, accuracy: number) {
    const accuracyMultiplier = Math.pow(10, accuracy);

    return Math.round(((celsius * 9) / 5 + 32) * accuracyMultiplier) / accuracyMultiplier;
  },

  kgcm2ToPSI(kgcm: number, accuracy: number) {
    const accuracyMultiplier = Math.pow(10, accuracy);
    return Math.round((kgcm * 14.2233) * accuracyMultiplier) / accuracyMultiplier;
  },

  PSITokgcm2(PSI: number, accuracy: number) {
    const accuracyMultiplier = Math.pow(10, accuracy);
    return Math.round((PSI / 14.2233) * accuracyMultiplier) / accuracyMultiplier;
  },

  MPaToPSI(MPa: number, accuracy: number) {
    const accuracyMultiplier = Math.pow(10, accuracy);
    return Math.round((MPa * 145.038) * accuracyMultiplier) / accuracyMultiplier;
  },

  PSIToMPa(PSI: number, accuracy: number) {
    const accuracyMultiplier = Math.pow(10, accuracy);
    return Math.round((PSI / 145.038) * accuracyMultiplier) / accuracyMultiplier;
  },

  MPaTokgcm2(MPa: number, accuracy: number) {
    const accuracyMultiplier = Math.pow(10, accuracy);
    return Math.round((MPa * 10.1972) * accuracyMultiplier) / accuracyMultiplier;
  },
  convertArrayToObject(array: any) {
    return array.reduce((object: any, item: any) => {
      object[item.id] = item;
      return object;
    }, {});
  },

  toHoursAndMinutes(totalMinutes: any) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })}`;
  },

  isValidDuration(duration: string) {
    return ((/^\d+:\d+$/g).test(duration) && (Number(duration.split(":")[1]) < 60)) || (/^\d+$/).test(duration);
  },

  isValidTime(time: string) {
    const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return regex.test(time);
  }
};

export default CommonUtils;

export const getActiveSchedulesCount = (
  schedules?: any[],
  allSchedules?: Record<string, any>
): number => {
  if (!Array.isArray(schedules) || schedules.length === 0) {
    return 0;
  }
  return schedules.filter((s: any) => {
    if (!s) {
      return false;
    }
    const id = typeof s === "object" ? s.id : s;
    if (id && allSchedules && allSchedules[id]) {
      const sched = allSchedules[id];
      return !sched.isDisabled && !sched.disabled && sched.status !== "inactive";
    }
    if (typeof s === "object") {
      return !s.isDisabled && !s.disabled && s.status !== "inactive";
    }
    return false;
  }).length;
};

/**
 * Counts a unit's active schedules, including schedules inherited from its
 * parent group(s) - not just the unit's own schedules. A group schedule that
 * is enabled counts as an active schedule for every unit in that group, so
 * the unit's badge shouldn't under-report just because the schedule lives on
 * the group rather than the unit itself.
 *
 * @param unit - The unit object (needs `schedules`, `groups`).
 * @param allSchedules - Map of schedule id -> schedule object.
 * @param allGroups - Map of group id -> group object (needs `schedules`), used to resolve `unit.groups`.
 */
export const getUnitActiveSchedulesCount = (
  unit: any,
  { allSchedules, allGroups }: { allSchedules?: Record<string, any>; allGroups?: Record<string, any> }
): number => {
  if (!unit) {
    return 0;
  }

  const ownCount = getActiveSchedulesCount(unit.schedules, allSchedules);

  const groupCount = (unit.groups || []).reduce((sum: number, groupId: string) => {
    const groupSchedules = allGroups?.[groupId]?.schedules || [];
    const uniqueGroupSchedules = groupSchedules.filter(
      (gs: any) => !(unit.schedules || []).find((us: any) => (typeof us === "object" ? us.id === gs : us === gs))
    );
    return sum + getActiveSchedulesCount(uniqueGroupSchedules, allSchedules);
  }, 0);

  return ownCount + groupCount;
};

export const isRouteSelected = (currentPath: string, itemPath: string): boolean => {
  if (!currentPath || !itemPath) {
    return false;
  }
  const normalizedCurrentPath = currentPath.replace(/\/+$/, "");
  const normalizedItemPath = itemPath.replace(/\/+$/, "");
  return normalizedCurrentPath === normalizedItemPath ||
    normalizedCurrentPath.startsWith(`${normalizedItemPath}/`);
};

export function makeFullAddressForSite(site: ISite) {
  if (!site) {
    return "";
  }
  let address = "";
  if (!site.country) return "";
  // US version !
  if (site.address) address += site.address;
  if (site.city) address += `, ${site.city}`;
  if (site.country === "US" && site.state) address += `, ${site.state}`;
  if (site.postalCode) address += `, ${site.postalCode}`;
  address += `, ${site.country}`;
  return address;
}

export function makeFullName({
  firstName,
  lastName
}: {
  firstName: string;
  lastName: string;
  [key: string]: any;
}) {
  if (!firstName) {
    if (!lastName) return "-";
    else return lastName;
  } else {
    return firstName + " " + lastName;
  }
}

export function generateRandomString() {
  let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < 5; i++) {
    let randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }

  return result;
}
